<template>
  <div
    data-test="graphics-multiMediaSettings"
  >
    <SidebarSettingsGroup
      title="Video Settings"
      collapsible
      :element-type="element.type"
      data-test="graphics-multiMediaSettings-videoSettings"
    >
      <div class="sidebar-form-group">
        <label class="checkbox">Loop
          <input
            v-model="loop"
            type="checkbox"
            data-test="graphics-multiMediaSettings-toggleLoop"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>
      </div>
    </SidebarSettingsGroup>
    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-multiMediaSettings-styleAndAnimation"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />
      <div class="sidebar-form-group">
        <div
          v-if="dataProvider"
          class="margin-top-md"
          data-test="graphics-multiMediaSettings-dataProvider"
        >
          <p
            v-if="isMapped('src')"
            class="sidebar-form-label"
          >
          <!-- little information for this iteration as we scope media switching/uploading component -->
            <span class="far fa-photo-video"></span> Media is From Data Provider
          </p>
        </div>
  
        <div class="margin-top-md">
          <div class="sidebar-form-group">
            <label class="sidebar-form-label">Fill type</label>
            <select
              v-model="fillType"
              :disabled="mask"
              data-test="graphics-multiMediaSettings-selectFillType"
            >
              <option value="stretch">
                Stretch
              </option>
              <option value="fit">
                Fit
              </option>
              <option value="fill">
                Fill
              </option>
            </select>
          </div>

          <hr class="sidebar-divider">

          <div class="sidebar-form-group">
            <label class="sidebar-form-label">Background color</label>
            <label
              class="radio inline-radio"
              for="solid"
            >Solid
              <input
                id="solid"
                v-model="fillStyle"
                type="radio"
                value="solid"
                data-test="graphics-multiMediaSettings-fillStyleSolid"
              >
              <div class="checkmark"></div>
            </label>
            <label
              class="radio inline-radio"
              for="gradient"
            >Gradient
              <input
                id="gradient"
                v-model="fillStyle"
                type="radio"
                value="gradient"
                data-test="graphics-multiMediaSettings-fillStyleGradient"
              >
              <div class="checkmark"></div>
            </label>
  
            <div
              v-if="fillStyle == 'solid'"
              class="colorpicker text-center"
              data-test="graphics-multiMediaSettings-fillStyleSolidColor"
            >
              <colorpicker
                v-model="backgroundColor"
                :is-mapped="isMapped('backgroundColor')"
              />
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span
                  v-if="isMapped('backgroundColor')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </div>
  
            <div
              v-else
              class="settings-row settings-row-xs"
              data-test="graphics-multiMediaSettings-fillStyleGradient"
            >
              <div class="settings-col-6 settings-col-xs">
                <div class="input-group">
                  <validation-field as="input"
                    v-model.number="gradientDirection"
                    rules="required|numeric"
                    type="number"
                    name="gradientDirection"
                    :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                    data-test="graphics-multiMediaSettings-setGradientDirection"
                    data-entity="graphics-multiMediaSettings-setGradientDirection"
                  />
                  <span class="input-group-addon symbol">°</span>
                </div>
              </div>
              <div class="settings-col-3 settings-col-xs colorpicker text-center">
                <colorpicker
                  v-model="gradientColorOne"
                  :is-mapped="isMapped('gradientColorOne')"
                />
                <span
                  data-balloon="This setting is controlled by data. Unmap data to customize."
                  data-balloon-pos="down"
                  data-balloon-length="medium"
                >
                  <span
                    v-if="isMapped('gradientColorOne')"
                    class="fa fa-lock mapped-lock"
                  />
                </span>
              </div>
              <div class="settings-col-3 settings-col-xs colorpicker text-center">
                <colorpicker
                  v-model="gradientColorTwo"
                  :is-mapped="isMapped('gradientColorTwo')"
                />
                <span
                  data-balloon="This setting is controlled by data. Unmap data to customize."
                  data-balloon-pos="down-right"
                  data-balloon-length="medium"
                >
                  <span
                    v-if="isMapped('gradientColorTwo')"
                    class="fa fa-lock mapped-lock"
                  />
                </span>
              </div>
            </div>
  
            <!-- Map control -->
            <!-- TODO: remove once sheets can have more than one thing mapped -->
            <div
              v-if="dataProvider && dataProvider?.type !== 'sheet-data'"
              class="margin-top-sm"
            >
              <p
                v-if="isBackgroundColorMapped"
                class="sidebar-form-label margin-top-sm"
              >
                From Data Provider
              </p>
  
              <map-data-control
                v-if="fillStyle == 'solid'"
                :data-provider="dataProvider"
                :element="element"
                :scroll-offset="scrollOffset"
                property="backgroundColor"
                :mapped="isMapped('backgroundColor')"
                @update-map="mapToField"
                @clear-map="clearMapping"
              />
              <map-data-control
                v-if="fillStyle == 'gradient'"
                :data-provider="dataProvider"
                :element="element"
                :scroll-offset="scrollOffset"
                property="gradientColorOne"
                :mapped="isMapped('gradientColorOne')"
                @update-map="mapToField"
                @clear-map="clearMapping"
              />
              <map-data-control
                v-if="fillStyle == 'gradient'"
                :data-provider="dataProvider"
                :element="element"
                :scroll-offset="scrollOffset"
                property="gradientColorTwo"
                :mapped="isMapped('gradientColorTwo')"
                @update-map="mapToField"
                @clear-map="clearMapping"
              />
            </div>
          </div>
          <hr class="sidebar-divider">
          <div class="sidebar-form-group">
            <label class="checkbox header-checkbox">Border
              <input
                v-model="border"
                type="checkbox"
                data-test="graphics-multiMediaSettings-toggleBorder"
              >
              <div class="checkmark">
                <span class="checkmark-tick fa-solid fa-check" />
              </div>
            </label>
  
            <div
              v-if="border"
              class="settings-row settings-row-xs"
            >
              <div class="settings-col-6 settings-col-xs">
                <validation-field as="input"
                  v-model.number="borderWidth"
                  rules="required|numeric|min:0"
                  type="number"
                  name="borderWidth"
                  min="0"
                  :class="{ 'is-invalid': validationErrors['borderWidth'] }"
                  data-test="graphics-multiMediaSettings-setBorderWidth"
                  data-entity="graphics-multiMediaSettings-setBorderWidth"
                />
                <label class="sidebar-input-sub-label">Width</label>
              </div>
              <div class="settings-col-6 settings-col-xs colorpicker">
                <colorpicker v-model="borderColor" />
                <label class="sidebar-input-sub-label">Color</label>
              </div>
            </div>
          </div>
  
          <hr class="sidebar-divider">
  
          <box-shadow-settings :element="element" />
  
          <hr class="sidebar-divider">
  
          <div class="sidebar-form-group">
            <label class="checkbox header-checkbox">Blur
              <input
                v-model="blur"
                type="checkbox"
              >
              <div class="checkmark">
                <span class="checkmark-tick fa-solid fa-check" />
              </div>
            </label>
  
            <div
              v-if="blur"
              class="input-group"
            >
              <validation-field as="input"
                v-model.number="blurSize"
                rules="required|numeric|min:0"
                type="number"
                name="blurSize"
                min="0"
                :class="{ 'is-invalid': validationErrors['blurSize'] }"
                data-test="graphics-multiMediaSettings-setBlurSize"
                data-entity="graphics-multiMediaSettings-setBlurSize"
              />
              <span class="input-group-addon">px</span>
            </div>
          </div>
  
          <hr class="sidebar-divider">
  
          <div class="sidebar-form-group">
            <label class="checkbox header-checkbox">Mask
              <input
                v-model.lazy="mask"
                type="checkbox"
                data-test="graphics-multiMediaSettings-toggleMask"
              >
              <div class="checkmark">
                <span class="checkmark-tick fa-solid fa-check" />
              </div>
            </label>
  
            <div class="sidebar-help-text">
              Image will fill the entire shape and ignore "Fill type" (set above).
            </div>
  
            <div
              v-if="mask"
              class="sidebar-sub-group"
            >
              <label class="sidebar-form-label">Shape</label>
              <select
                v-model="maskShape"
                data-test="graphics-multiMediaSettings-selectMaskShape"
                data-entity="graphics-multiMediaSettings-selectMaskShape"
              >
                <option value="oval">
                  Oval
                </option>
                <option value="rectangle">
                  Rectangle
                </option>
              </select>
            </div>
  
            <div
              v-if="mask && maskShape === 'rectangle'"
              class="sidebar-sub-group"
            >
              <border-radius
                v-model:top-left="borderTopLeftRadius"
                v-model:top-right="borderTopRightRadius"
                v-model:bottom-left="borderBottomLeftRadius"
                v-model:bottom-right="borderBottomRightRadius"
              />
            </div>
  
            <div
              v-if="mask"
              class="sidebar-sub-group position"
              data-test="graphics-multiMediaSettings-maskPosition"
            >
              <label class="sidebar-form-label">Position</label>
  
              <div class="mask-position-controls">
                <button
                  v-for="p in maskPositions"
                  :key="p"
                  class="mask-position-controls__control"
                  :class="{
                    'center': p === 'center-center',
                    'selected': maskPosition === p,
                  }"
                  :data-test="`graphics-multiMediaSettings-maskPosition-${p}`"
                  :data-entity="`graphics-multiMediaSettings-maskPosition-${p}`"
                  @click="maskPosition = p"
                >
                  <span class="sr-only">{{ p }}</span>
                </button>
              </div>
            </div>
          </div>
  
          <hr class="sidebar-divider">
  
          <animation-settings :element="element" />
        </div>
      </div>
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import MapDataControl from './Data/MapDataControl.vue';
import BorderRadius from './BorderRadius.vue';

export default {
  emits: ['modal'],
  name: 'MultiMediaSettings',
  components: {
    AnimationSettings,
    OpacitySettings,
    BoxShadowSettings,
    MapDataControl,
    BorderRadius,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],

  data() {
    return {
      // mediaType: 'image',
      maskPositions: [
        'top-left',
        'top-center',
        'top-right',
        'center-left',
        'center-center',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ],
    };
  },
  computed: {
    mediaType: {
      get() {
        return this.element.isVideo ? 'video' : 'image';
      },
      set(v) {
        this.isVideo = v === 'video';
      },
    },
    isVideo: {
      get() {
        return (this.isMapped('isVideo') && this.dataValue('isVideo')) || this.element.isVideo;
      },
      set(v) {
        this.updateOneElementValue('isVideo', v);
      },
    },
    loop: {
      get() {
        return this.element.videoOptions.loop;
      },
      set(v) {
        this.updateOneElementValue('videoOptions.loop', v);
      },
    },
    opacity: {
      get() {
        return this.element.styles.opacity;
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    src: {
      get() {
        return (this.isMapped('src') && this.dataValue('src')) || this.element.src;
      },
      set(v) {
        this.updateOneElementValue('src', v);
      },
    },
    fillType: {
      get() {
        return this.element.fillType;
      },
      set(v) {
        this.updateOneElementValue('fillType', v);
      },
    },
    fillStyle: {
      get() {
        return this.element.fillStyle;
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return (this.isMapped('backgroundColor') && this.dataValue('backgroundColor')) || this.element.backgroundColor;
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.element.gradientDirection;
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return (this.isMapped('gradientColorOne') && this.dataValue('gradientColorOne')) || this.element.gradientColorOne;
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return (this.isMapped('gradientColorTwo') && this.dataValue('gradientColorTwo')) || this.element.gradientColorTwo;
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },
    border: {
      get() {
        return this.element.border;
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },
    borderWidth: {
      get() {
        return this.element.borderWidth;
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },
    borderColor: {
      get() {
        return (this.isMapped('borderColor') && this.dataValue('borderColor')) || this.element.borderColor;
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },
    blur: {
      get() {
        return this.element.blur;
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },
    blurSize: {
      get() {
        return this.element.blurSize;
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
    mask: {
      get() {
        return this.element.mask;
      },
      set(v) {
        this.updateOneElementValue('mask', v);
      },
    },
    maskShape: {
      get() {
        return this.element.maskShape;
      },
      set(v) {
        this.updateOneElementValue('maskShape', v);
      },
    },
    maskPosition: {
      get() {
        return this.element.maskPosition ?? 'center-center';
      },
      set(v) {
        this.updateOneElementValue('maskPosition', v);
      },
    },
    borderTopLeftRadius: {
      get() {
        return this.element.borderTopLeftRadius;
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },
    borderTopRightRadius: {
      get() {
        return this.element.borderTopRightRadius;
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },
    borderBottomRightRadius: {
      get() {
        return this.element.borderBottomRightRadius;
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },
    borderBottomLeftRadius: {
      get() {
        return this.element.borderBottomLeftRadius;
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },
  },
  methods: {
    removeSrc() {
      this.src = '';
    },
    selectAsset() {
      let type = 'image';
      if (!this.isMapped('src')) {
        type = this.mediaType;
      }

      this.$emit('modal', { open: true, type });

      window.assetmanfn = ({ asset }) => {
        this.src = asset?.media_url || '';
        this.$emit('modal', { open: false });
      };
    },
  },
};
</script>
